/**
 * Flight Entry Page
 * Log new flights and view recent entries
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useFlights, useCreateFlight } from '@/hooks/useFlights';
import { useAircraftOptions } from '@/hooks/useAircraft';
import { useTrips } from '@/hooks/useTrips';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import DatePicker from '@/components/ui/DatePicker';
import { Card, CardHeader, CardTitle, CardBody } from '@/components/ui/Card';
import { PlusIcon, CalendarIcon, PencilIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface FlightFormData {
  flightDate: string;
  tailNumber: string;
  logsheetNumber: string;
  qNumber: string;
  airTime: string;
  flightTime: string;
  isShortNotice: boolean;
}

const initialFormData: FlightFormData = {
  flightDate: new Date().toISOString().split('T')[0], // Today's date
  tailNumber: '',
  logsheetNumber: '',
  qNumber: '',
  airTime: '',
  flightTime: '',
  isShortNotice: false,
};

export default function FlightEntryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [formData, setFormData] = useState<FlightFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<FlightFormData>>({});

  // Fetch aircraft options from API
  const { data: aircraftOptions = [] } = useAircraftOptions();

  // Fetch pilot's assigned trips for pre-fill
  const { data: trips = [] } = useTrips();

  // Active trips for the dropdown (not completed/cancelled)
  const activeTrips = trips.filter(t =>
    t.status !== 'COMPLETED' && t.status !== 'CANCELLED'
  );

  const handleTripSelect = (tripId: string) => {
    if (!tripId) return;
    const trip = trips.find(t => t.id === parseInt(tripId));
    if (trip) {
      setFormData(prev => ({
        ...prev,
        qNumber: trip.qNumber,
        tailNumber: trip.tailNumber || prev.tailNumber,
        flightDate: trip.startDate ? new Date(trip.startDate).toISOString().split('T')[0] : prev.flightDate,
      }));
    }
  };

  // Fetch user's flights
  const { data: flights, isLoading: isLoadingFlights } = useFlights(user?.id);

  // Create flight mutation
  const createFlightMutation = useCreateFlight();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error for this field when user starts typing
    if (errors[name as keyof FlightFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FlightFormData> = {};

    if (!formData.flightDate) newErrors.flightDate = 'Flight date is required';
    if (!formData.tailNumber) newErrors.tailNumber = 'Tail number is required';
    if (!formData.logsheetNumber) newErrors.logsheetNumber = 'Logsheet number is required';
    if (!formData.qNumber) newErrors.qNumber = 'Q number is required';
    if (!formData.airTime) {
      newErrors.airTime = 'Air time is required';
    } else if (parseFloat(formData.airTime) <= 0) {
      newErrors.airTime = 'Air time must be greater than 0';
    }
    if (!formData.flightTime) {
      newErrors.flightTime = 'Flight time is required';
    } else if (parseFloat(formData.flightTime) <= 0) {
      newErrors.flightTime = 'Flight time must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      showError('Not authenticated', 'Please log in to log flights');
      return;
    }

    if (!validateForm()) {
      showError('Validation Error', 'Please fix the errors in the form');
      return;
    }

    try {
      await createFlightMutation.mutateAsync({
        userId: user.id,
        flightDate: formData.flightDate,
        tailNumber: formData.tailNumber,
        logsheetNumber: formData.logsheetNumber,
        qNumber: formData.qNumber,
        airTime: parseFloat(formData.airTime),
        flightTime: parseFloat(formData.flightTime),
        isShortNotice: formData.isShortNotice,
      });

      showSuccess(
        'Flight Logged Successfully',
        `${formData.airTime}h flight on ${formData.tailNumber}`
      );

      // Reset form
      setFormData(initialFormData);
      setErrors({});
    } catch (error: any) {
      showError(
        'Failed to Log Flight',
        error?.response?.data?.error || error?.message || 'Please try again'
      );
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Flight Entry</h1>
        <p className="text-foreground/60">Log your flights and track your hours</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Flight Entry Form */}
        <div className="lg:col-span-2">
          <Card variant="elevated">
            <CardHeader>
              <CardTitle>Log New Flight</CardTitle>
            </CardHeader>
            <CardBody>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Trip Selector */}
                {activeTrips.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Load from Trip</label>
                    <select
                      onChange={(e) => handleTripSelect(e.target.value)}
                      className="w-full px-3 py-2 border border-border bg-background text-foreground rounded-md"
                      defaultValue=""
                    >
                      <option value="">Select a trip to pre-fill...</option>
                      {activeTrips.map(trip => (
                        <option key={trip.id} value={trip.id}>
                          {trip.qNumber} — {trip.tailNumber || 'No aircraft'} ({new Date(trip.startDate).toLocaleDateString()})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Flight Date */}
                <DatePicker
                  label="Flight Date"
                  name="flightDate"
                  value={formData.flightDate}
                  onChange={handleInputChange}
                  error={errors.flightDate}
                  required
                  max={new Date().toISOString().split('T')[0]} // Can't log future flights
                />

                {/* Aircraft Selection */}
                <Select
                  label="Aircraft"
                  name="tailNumber"
                  value={formData.tailNumber}
                  onChange={handleInputChange}
                  options={aircraftOptions}
                  placeholder="Select aircraft..."
                  error={errors.tailNumber}
                  required
                />

                {/* Logsheet & Q Numbers */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Logsheet Number"
                    name="logsheetNumber"
                    value={formData.logsheetNumber}
                    onChange={handleInputChange}
                    placeholder="LOG-001"
                    error={errors.logsheetNumber}
                    required
                  />

                  <Input
                    label="Q Number"
                    name="qNumber"
                    value={formData.qNumber}
                    onChange={handleInputChange}
                    placeholder="Q-12345"
                    error={errors.qNumber}
                    required
                  />
                </div>

                {/* Flight Hours */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Air Time (hours)"
                    name="airTime"
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.airTime}
                    onChange={handleInputChange}
                    placeholder="3.5"
                    error={errors.airTime}
                    helpText="Actual flying time"
                    required
                  />

                  <Input
                    label="Flight Time (hours)"
                    name="flightTime"
                    type="number"
                    step="0.1"
                    min="0"
                    value={formData.flightTime}
                    onChange={handleInputChange}
                    placeholder="4.0"
                    error={errors.flightTime}
                    helpText="Total duty time"
                    required
                  />
                </div>

                {/* Short Notice Checkbox */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isShortNotice"
                    name="isShortNotice"
                    checked={formData.isShortNotice}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
                  />
                  <label htmlFor="isShortNotice" className="text-sm text-foreground">
                    Short Notice Flight (less than 24 hours notice)
                  </label>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button
                    type="submit"
                    icon={<PlusIcon className="w-5 h-5" />}
                    iconPosition="left"
                    loading={createFlightMutation.isPending}
                    fullWidth
                    size="lg"
                  >
                    {createFlightMutation.isPending ? 'Logging Flight...' : 'Log Flight'}
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>
        </div>

        {/* Recent Flights Sidebar */}
        <div>
          <Card variant="elevated">
            <CardHeader>
              <CardTitle>Recent Flights</CardTitle>
            </CardHeader>
            <CardBody>
              {isLoadingFlights ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 bg-muted animate-pulse rounded" />
                  ))}
                </div>
              ) : !flights || flights.length === 0 ? (
                <div className="text-center py-8">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-foreground/40" />
                  <p className="text-sm text-foreground/60">No flights logged yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {flights.slice(0, 5).map((flight) => (
                    <div
                      key={flight.id}
                      className="p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                    >
                      <div className="flex justify-between items-start mb-1">
                        <p className="text-sm font-semibold text-foreground">
                          {flight.tailNumber}
                        </p>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-foreground/60">
                            {new Date(flight.flightDate).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                            })}
                          </p>
                          <button
                            onClick={() => navigate(`/flights/${flight.id}/edit`)}
                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-primary/10 rounded transition-all"
                            aria-label="Edit flight"
                          >
                            <PencilIcon className="w-4 h-4 text-primary" />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-foreground/60 mb-1">
                        {flight.logsheetNumber} • {flight.qNumber}
                      </p>
                      <div className="flex justify-between items-center">
                        <p className="text-xs font-medium text-foreground">
                          {flight.airTime}h air time
                        </p>
                        {flight.isShortNotice && (
                          <span className="text-xs px-2 py-0.5 rounded bg-warning/10 text-warning font-medium">
                            SN
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}
